// Singleton for user analytics with Segment
// For usage see the package README
// For Segment see https://segment.com/docs/sources/website/analytics.js/quickstart

import { v4 } from "uuid";
import { getAnalytics } from "./SegmentAnalytics";
import { axiosTracker, IAxiosTracker } from "./AxiosTracker";
import { AxiosResponse } from "axios";
import { getUserType, UserType } from "../../helpers";
import { getCurrentOperationId } from "../../next/hooks";

let appName = "";
let operationId: string | null = null;
let fieldId: string | null = null;
let userType: UserType | null = null;
const platform = "web";

const axiosTrackers: IAxiosTracker[] = [
  {
    matcher: {
      method: "post",
      success: true,
      urlSubstring: "/api/fields-svc/field",
    }, // save field
    responseEmitter: (response: AxiosResponse<{ id: string; area: number }>) =>
      segmentService.track("add field", {
        fieldId: response.data.id,
        fieldAreaMeterSquare: response.data.area,
      }),
  },
];

const shouldNotWarn =
  (window as any).NO_SEGMENT_SERVICE_WARNING || process.env.NODE_ENV === "test";
const shouldWarn = !shouldNotWarn;

if (shouldWarn && !getAnalytics()) {
  // tslint:disable-next-line:no-console
  console.warn(
    "Warning analytics.js not present so Segment tracking disabled.",
  );
}

export class SegmentService {
  constructor() {
    axiosTracker.registerTrackerArray(axiosTrackers);
  }

  public identify(userId: string, traits?: Record<string, any>): void {
    const analytics = getAnalytics();
    if (analytics) {
      const standardTraits = this.getStandardTraits();
      analytics.identify(
        userId,
        { ...standardTraits, ...traits },
        {
          integrations: this.getIntegrations(),
        },
      );
      userType = getUserType(traits?.email);
    }
  }

  public track(what: string, traits?: object): void {
    traits = traits || {};
    const analytics = getAnalytics();
    if (analytics) {
      const standardTraits = this.getStandardTraits();
      analytics.track(
        what,
        { ...standardTraits, ...traits },
        {
          integrations: this.getIntegrations(),
        },
      );
    }
  }

  public page(): void {
    const analytics = getAnalytics();
    if (analytics) {
      analytics.page();
    }
  }

  public group(groupId: string, traits?: Record<string, any>): void {
    traits = traits || {};
    const analytics = getAnalytics();
    if (analytics) {
      analytics.group(groupId, traits);
    }
  }

  public setAppName(newAppName: string): void {
    appName = newAppName;
  }

  public setOperation(operationIdParam: string | null, doTrack = true): void {
    if (operationIdParam === operationId) {
      return;
    }
    operationId = operationIdParam;
    fieldId = null;
    if (doTrack) {
      this.track("select operation", { operationId });
    }
  }

  public setField(fieldIdParam: string | null, doTrack = true): void {
    if (fieldIdParam === fieldId) {
      return;
    }
    fieldId = fieldIdParam;
    if (doTrack) {
      this.track("select field", { fieldId });
    }
  }

  private getIntegrations() {
    return {
      Amplitude: {
        session_id: this.getSessionTimeStamp(),
      },
    };
  }

  private getSessionTimeStamp(): string {
    const key = "timestamp-for-tracking";
    if (!window || !window.sessionStorage) {
      return "-1";
    }
    let timestamp = window.sessionStorage.getItem(key);
    if (!timestamp) {
      timestamp = new Date().getTime().toString();
      window.sessionStorage.setItem(key, timestamp);
    }
    return timestamp;
  }

  private getSessionUuid(key: string): string | null {
    if (!window || !window.sessionStorage) {
      return null;
    }
    let sessionId: string | null = window.sessionStorage.getItem(key) || null;
    if (!sessionId) {
      sessionId = v4();
      window.sessionStorage.setItem(key, sessionId);
    }
    return sessionId;
  }

  private getAnonymousId(): string | null {
    return this.getSessionUuid("anonymous-id-for-tracking");
  }

  private getSessionId(): string | null {
    return this.getSessionUuid("session-id-for-tracking");
  }

  private getStandardTraits(): { [key: string]: string | null } {
    const traits: { [key: string]: string | null } = {
      sessionId: this.getSessionId(),
      anonymousId: this.getAnonymousId(),
      appName,
      platform,
      // Recoil is the source of truth for current operation id
      // If there is an operation set the value to be sent with all tracking events
      operationId: getCurrentOperationId() || null,
    };

    if (fieldId) {
      traits.fieldId = fieldId;
    }
    if (userType) {
      traits.userType = userType;
    }

    return traits;
  }
}

export const segmentService = new SegmentService();
